'use client'

import Link from 'next/link'
import { ArrowRight, Brain, Clock, BookMarked } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { analytics } from '@/lib/analytics'

export default function StudentsPage() {
  return (
    <div className="bg-background text-charcoal">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-background py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto animate-fade-in">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Learn faster.
              <br />
              <span className="text-primary">Actually understand it.</span>
            </h1>
            <p className="text-lg sm:text-xl text-warm-gray mb-8 leading-relaxed">
              Get 24/7 help understanding hard concepts. Bounce ideas. Write better essays. Study smarter, not harder.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/signup" onClick={() => analytics.ctaClicked({ location: 'hero', cta: 'signup', persona: 'student' })}>
                <Button className="bg-primary hover:bg-primary-700 text-white px-8 py-6 text-lg">
                  Get Started Free
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Button variant="outline" className="border-warm-gray text-charcoal hover:bg-primary/5 px-8 py-6 text-lg" asChild>
                <a href="#for-you" onClick={() => analytics.ctaClicked({ location: 'hero', cta: 'learn_more', persona: 'student' })}>How it helps</a>
              </Button>
            </div>
            <p className="text-sm text-warm-gray mt-4">No credit card required &middot; 5 free sessions included</p>
          </div>
        </div>
      </section>

      {/* For You Section */}
      <section id="for-you" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Help whenever you need it</h2>
            <p className="text-warm-gray text-lg">Like a tutor who never sleeps and doesn't judge.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Understand concepts */}
            <div className="p-8 bg-background rounded-lg border border-primary/10">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary mb-4">
                <Brain className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Finally get it</h3>
              <p className="text-warm-gray leading-relaxed">
                That concept your teacher explained 3 times and you still don't get? Ask AI. Different explanation. Actually clicks.
              </p>
            </div>

            {/* Write better */}
            <div className="p-8 bg-background rounded-lg border border-primary/10">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary mb-4">
                <BookMarked className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Write smarter essays</h3>
              <p className="text-warm-gray leading-relaxed">
                Stuck on an essay? Get help structuring your argument, finding sources, making your point clearer. You do the thinking, AI helps you write.
              </p>
            </div>

            {/* Study efficiently */}
            <div className="p-8 bg-background rounded-lg border border-primary/10">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary mb-4">
                <Clock className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Study smarter</h3>
              <p className="text-warm-gray leading-relaxed">
                Create study guides, quiz yourself, explore topics deeper. More learning in less time because you're actually understanding, not just cramming.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 sm:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">How students are using it</h2>
            <p className="text-warm-gray text-lg">Real examples from real studying.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Understand concepts in different ways',
                description: "Your textbook explanation didn't stick? Ask AI to explain it like you're 10, then deeper. When it clicks, you got it.",
                example: 'You: "Explain photosynthesis"\nAI: Simple explanation + why it matters + why plants need it'
              },
              {
                title: 'Get feedback on your writing',
                description: 'Write your essay. Ask AI what\'s missing, where your argument is weak, how to strengthen it. Real feedback before you submit.',
                example: 'You: [Paste your draft]\nAI: "Strong opening, but here\'s where you lost me..." + suggestions'
              },
              {
                title: 'Study for exams efficiently',
                description: 'Instead of re-reading chapters, create study guides. Ask questions. Get AI quizzes on the material. Study what matters.',
                example: 'You: "Quiz me on World War II"\nAI: [Generates questions + explains wrong answers]'
              },
              {
                title: 'Research better, faster',
                description: 'Need to understand a topic for a paper? Ask AI to break it down, suggest angles, help you find what actually matters.',
                example: 'You: "Explain climate change for a research paper"\nAI: Overview + key arguments + things to investigate'
              }
            ].map((useCase, i) => (
              <div key={i} className="p-8 bg-white rounded-lg border border-gray-200">
                <h3 className="text-xl font-semibold mb-3 text-charcoal">{useCase.title}</h3>
                <p className="text-warm-gray mb-6 leading-relaxed">{useCase.description}</p>
                <div className="bg-background p-4 rounded border border-primary/10">
                  <p className="text-sm font-mono text-charcoal whitespace-pre-wrap">{useCase.example}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Students are learning better</h2>
            <p className="text-warm-gray text-lg">See what students are doing</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: 'I\'ve asked my professor the same question 3 times. AI finally explained it in a way that made sense. Wish I had this all semester.',
                author: 'Jordan Davis',
                role: 'College sophomore, Biology major',
                initials: 'JD',
                avatarClass: 'bg-teal-100 text-teal-700',
              },
              {
                quote: 'Writing essays used to take me 4 hours. Now I spend an hour writing, and AI helps me structure and polish it. My grades went up too.',
                author: 'Amara Osei',
                role: 'High school student',
                initials: 'AO',
                avatarClass: 'bg-rose-100 text-rose-700',
              },
              {
                quote: 'Study guides used to be me just rewriting the textbook. Now I quiz myself on what I actually don\'t understand. Test prep is way better.',
                author: 'Casey Rodriguez',
                role: 'Graduate student',
                initials: 'CR',
                avatarClass: 'bg-lime-100 text-lime-700',
              },
            ].map((testimonial, i) => (
              <div key={i} className="p-8 bg-background rounded-lg border border-gray-200">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <span key={j} className="text-warning">★</span>
                  ))}
                </div>
                <p className="text-charcoal mb-6 leading-relaxed italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${testimonial.avatarClass} flex items-center justify-center text-sm font-semibold flex-shrink-0`}>
                    {testimonial.initials}
                  </div>
                  <div>
                    <p className="font-semibold text-charcoal">{testimonial.author}</p>
                    <p className="text-sm text-warm-gray">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 sm:py-28 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Student questions we hear</h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: 'Is using AI cheating?',
                a: "Using AI as a tutor to help you understand? No. Copying AI's essay and submitting it? Yes. Use AI to learn, understand, and improve your thinking. Don't use it to skip the thinking. If your school has an AI policy, follow it. But using AI to understand a tough concept is always okay."
              },
              {
                q: 'Will my teacher know I used AI?',
                a: 'If you submit AI-written work verbatim, probably yes. If you use AI to learn and then write in your own words? No. Use AI to understand, then explain it back in your own words. That\'s real learning anyway.'
              },
              {
                q: 'Is this going to make me dumb?',
                a: 'Opposite. You use AI to understand the confusing parts faster, then dive deeper into what actually interests you. Less time stuck on fundamentals, more time on the interesting stuff.'
              },
              {
                q: 'My parents worry about my privacy.',
                a: "Don't paste personal information. Treat it like you would talking to a classmate—don't share stuff you want to keep private. Your school chat is between you and the AI. We don't read your sessions or sell data."
              }
            ].map((faq, i) => (
              <div key={i} className="p-6 bg-white rounded-lg border border-gray-200">
                <h3 className="font-semibold text-charcoal mb-3 text-lg">{faq.q}</h3>
                <p className="text-warm-gray leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Get help 24/7</h2>
          <p className="text-lg text-warm-gray mb-8">
            Learn faster. Understand better. Actually feel ready for that test.
          </p>

          <Link href="/signup" onClick={() => analytics.ctaClicked({ location: 'footer_cta', cta: 'signup', persona: 'student' })}>
            <Button className="bg-primary hover:bg-primary-700 text-white px-8 py-6 text-lg">
              Get Started Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  )
}
